import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import { useChat, ChatMessage } from "../contexts/ChatContext";
import { useAuth } from "../../auth/contexts/AuthContext";

interface ChatSidebarProps {
  embedded?: boolean;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const {
    messages,
    sendMessage,
    isChatOpen,
    setIsChatOpen,
    unreadCount,
    resetUnread,
    clearHistory,
    activeUsers,
  } = useChat();
  const { user } = useAuth();
  const [inputText, setInputText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [target, setTarget] = useState<string>("Global"); // 'Global' or username
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  const isOnlyEmoji = (text: string) => {
    // Simple but effective check for a single emoji
    const trimmed = text.trim();
    const emojiRegex =
      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
    const matches = trimmed.match(emojiRegex);
    // It's only an emoji message if there's exactly one emoji and its length (with modifiers) matches the trimmed text
    return matches !== null && matches.length === 1 && trimmed.length <= 8; // 8 is safe for most modifier combos
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    if (isChatOpen) {
      resetUnread();
    }
  }, [messages, isChatOpen, resetUnread]);

  // Close emoji picker on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (inputText.trim()) {
      const replyData = replyingTo
        ? {
            messageId: replyingTo.id,
            author: replyingTo.author,
            text: replyingTo.text,
            tc: replyingTo.tc,
          }
        : undefined;

      sendMessage(inputText.trim(), target, replyData);

      // Reset state
      setInputText("");
      setShowEmoji(false);
      setReplyingTo(null);
      // setTarget('Global'); // Eliminado para mantener el destinatario seleccionado
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setInputText((prev) => prev + emojiData.emoji);
  };

  const handleCopyResponse = () => {
    sendMessage("COPY", target);
  };

  const getRoleColor = (role: string) => {
    if (!role) return "var(--color-accent-danger)";
    switch (role) {
      case "Content Controller":
        return "#38BDF8";
      case "Content Director":
        return "var(--color-role-contentdirector)";
      case "Story Producer":
        return "#ffcc00";
      case "Showrunner":
        return "var(--color-role-showrunner)";
      case "Logger":
        return "#f72d60";
      default:
        return "var(--color-accent-danger)";
    }
  };

  if (!isChatOpen && !embedded) {
    // Don't show tab if simple embedded
    return (
      <button
        onClick={() => setIsChatOpen(true)}
        style={{
          position: "fixed",
          right: "0",
          bottom: "20px",
          padding: "12px 24px 12px 16px",
          backgroundColor: "var(--color-bg-base)",
          color: "white",
          border: "1px solid var(--color-panel-border)",
          borderRight: "none",
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
          boxShadow: "-4px 4px 20px rgba(0,0,0,0.5)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          zIndex: 2000,
          transition: "all 0.3s ease",
          fontWeight: "700",
          letterSpacing: "1px",
          fontSize: "var(--text-base)",
        }}
        data-testid="chat-toggle-tab"
        className="chat-toggle-tab"
        onMouseEnter={(e) => (e.currentTarget.style.transform = "translateX(-5px)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "translateX(0)")}
      >
        <div style={{ position: "relative" }}>
          <i
            className="bx bx-message-square-dots"
            style={{ fontSize: "1.4rem", color: "var(--color-accent-danger)" }}
          ></i>
          {unreadCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                backgroundColor: "var(--color-accent-danger)",
                color: "white",
                borderRadius: "50%",
                width: "16px",
                height: "16px",
                fontSize: "var(--text-2xs)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid var(--color-bg-base)",
              }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <span>{t("common.chat.chat")}</span>
      </button>
    );
  }

  return (
    <div
      style={{
        position: embedded ? "relative" : "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        width: embedded ? "100%" : "350px",
        height: embedded ? "100%" : "auto",
        backgroundColor: "var(--color-panel-bg)",
        borderRadius: embedded ? "var(--border-radius-md)" : "0",
        border: embedded ? "1px solid var(--color-panel-border)" : "none",
        borderLeft: !embedded ? "1px solid var(--color-panel-border)" : undefined,
        zIndex: embedded ? 1 : 3000,
        display: "flex",
        flexDirection: "column",
        boxShadow: embedded ? "none" : "-20px 0 50px rgba(0,0,0,0.7)",
        animation: embedded ? "none" : "slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        overflow: "hidden",
      }}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div
        data-testid="chat-header"
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--color-panel-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--color-bg-base)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <i
            className="bx bx-chat"
            style={{ fontSize: "1.2rem", color: "var(--color-accent-danger)" }}
          ></i>
          <h3
            style={{
              margin: 0,
              fontSize: "var(--text-md)",
              fontWeight: "800",
              letterSpacing: "2px",
            }}
          >
            {t("common.chat.chat")}
          </h3>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          {(user?.role === "Content Controller" || user?.role === "Content Director") && (
            <button
              data-testid="chat-btn-clear-history"
              onClick={clearHistory}
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.4)",
                cursor: "pointer",
                width: "30px",
                height: "30px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
              title={t("common.chat.clearHistory")}
              className="hover-btn"
            >
              <i className="bx bx-trash" style={{ fontSize: "1.2rem" }}></i>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        data-testid="chat-messages"
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          scrollBehavior: "smooth",
        }}
        className="thin-scrollbar"
      >
        {messages.length === 0 ? (
          <div
            data-testid="chat-empty-state"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.2)",
              gap: "10px",
            }}
          >
            <i className="bx bx-message-dots" style={{ fontSize: "3rem", opacity: 0.5 }}></i>
            <span style={{ fontSize: "var(--text-sm)", fontWeight: "600", letterSpacing: "1px" }}>
              {t("common.chat.startConversation")}
            </span>
          </div>
        ) : (
          messages.map((msg, idx) => {
            // Filter Logic:
            // Show if global (msg.recipient is 'Global' or undefined) OR if I am the author OR if I am the recipient
            const myName = (user?.name || "").toUpperCase();
            const isGlobal = !msg.recipient || msg.recipient === "Global";
            const isForMe = (msg.recipient || "").toUpperCase() === myName;
            const isMine = (msg.author || "").toUpperCase() === myName;

            // Only show if Global, For Me, or Mine
            if (!isGlobal && !isForMe && !isMine) return null;

            const isMe = (msg.author || "").toUpperCase() === myName;
            const showAuthor = idx === 0 || messages[idx - 1].author !== msg.author;
            const isDirect = !isGlobal;

            return (
              <div
                key={msg.id}
                data-testid={`chat-message-${idx}`}
                style={{
                  alignSelf: isMe ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  const replyBtn = e.currentTarget.querySelector(".reply-btn") as HTMLElement;
                  if (replyBtn) replyBtn.style.opacity = "1";
                }}
                onMouseLeave={(e) => {
                  const replyBtn = e.currentTarget.querySelector(".reply-btn") as HTMLElement;
                  if (replyBtn) replyBtn.style.opacity = "0";
                }}
              >
                {showAuthor && !isMe && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginLeft: "4px",
                      marginBottom: "2px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "800",
                        color: getRoleColor(msg.role),
                      }}
                    >
                      {msg.author}
                    </span>
                    <span
                      style={{
                        fontSize: "var(--text-2xs)",
                        border: `1px solid ${getRoleColor(msg.role)}`,
                        color: getRoleColor(msg.role),
                        padding: "0 3px",
                        borderRadius: "3px",
                        opacity: 0.8,
                        fontWeight: "700",
                      }}
                    >
                      {(msg.role || "").toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Reply Context */}
                {msg.replyTo && (
                  <div
                    onClick={() => {
                      const targetId =
                        typeof msg.replyTo === "object" ? msg.replyTo?.messageId : null;
                      if (targetId) {
                        const el = document.getElementById(`msg-${targetId}`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                    }}
                    style={{
                      fontSize: "var(--text-sm)",
                      background: "rgba(255, 255, 255, 0.07)",
                      padding: "6px 10px",
                      borderRadius: "8px 8px 0 0",
                      marginBottom: "-1px",
                      borderLeft: `3px solid ${isMe ? "var(--color-accent-danger)" : "var(--color-accent-warning)"}`,
                      opacity: 0.9,
                      width: "100%",
                      alignSelf: isMe ? "flex-end" : "flex-start",
                      position: "relative",
                      zIndex: 1,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.07)")
                    }
                  >
                    <div
                      style={{
                        fontWeight: "900",
                        fontSize: "var(--text-2xs)",
                        marginBottom: "2px",
                        color: isMe ? "var(--color-accent-danger)" : "var(--color-accent-warning)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "6px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="bx bx-reply" style={{ transform: "scaleX(-1)" }}></i>
                        {(msg.replyTo.author || "").toUpperCase() === myName
                          ? t("common.chat.replyingToYou")
                          : t("common.chat.replyingTo", {
                              author: msg.replyTo.author.toUpperCase(),
                            })}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          opacity: 0.6,
                          fontSize: "var(--text-2xs)",
                        }}
                      >
                        {msg.replyTo.tc}
                      </span>
                    </div>
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "var(--text-xs)",
                        color: "rgba(255,255,255,0.7)",
                        fontStyle: "italic",
                      }}
                    >
                      "{msg.replyTo.text}"
                    </div>
                  </div>
                )}

                <div
                  id={`msg-${msg.id}`}
                  style={{
                    padding: isOnlyEmoji(msg.text) ? "8px 0 20px 0" : "8px 10px 14px 10px",
                    borderRadius: isMe ? "12px 12px 2px 12px" : "2px 12px 12px 12px",
                    background: isOnlyEmoji(msg.text)
                      ? "transparent"
                      : isMe
                        ? isDirect
                          ? "rgba(20, 20, 20, 0.95)" // Fondo oscuro para privado (sent)
                          : "linear-gradient(135deg, var(--color-accent-danger) 0%, #d62250 100%)" // Fondo rosa para global (sent)
                        : isDirect
                          ? "rgba(255, 180, 0, 0.05)"
                          : "var(--color-bg-base)",
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: isOnlyEmoji(msg.text) ? "3.5rem" : "0.8rem",
                    textAlign: isOnlyEmoji(msg.text) ? "center" : "left",
                    lineHeight: "1.4",
                    boxShadow: isOnlyEmoji(msg.text)
                      ? "none"
                      : isMe
                        ? isDirect
                          ? "0 0 10px rgba(0, 208, 132, 0.15)"
                          : "0 4px 15px rgba(247, 45, 96, 0.2)"
                        : isDirect
                          ? "0 0 10px rgba(255, 180, 0, 0.08)"
                          : "0 2px 8px rgba(0,0,0,0.25)",
                    position: "relative",
                    minWidth: isOnlyEmoji(msg.text) ? "50px" : "70px",
                    border: isOnlyEmoji(msg.text)
                      ? "none"
                      : isMe
                        ? isDirect
                          ? "1.5px solid var(--color-accent-success)" // Marco verde para privado (sent)
                          : "1px solid rgba(255,255,255,0.15)"
                        : isDirect
                          ? "1px solid var(--color-accent-warning)" // Marco amarillo para privado (received)
                          : "1px solid var(--color-panel-border)",
                    backdropFilter: isOnlyEmoji(msg.text) ? "none" : "blur(12px)",
                    zIndex: 2,
                    transition: "all 0.2s ease",
                  }}
                >
                  {isDirect && (
                    <div
                      style={{
                        fontSize: "var(--text-2xs)",
                        opacity: 0.8,
                        marginBottom: "3px",
                        fontWeight: "800",
                        letterSpacing: "0.5px",
                        color: isMe ? "rgba(255,255,255,0.9)" : "var(--color-accent-warning)",
                      }}
                    >
                      <i
                        className={`bx ${isMe ? "bx-lock-open" : "bx-lock"}`}
                        style={{ marginRight: "3px", fontSize: "var(--text-2xs)" }}
                      ></i>
                      {isMe
                        ? t("common.chat.to", { recipient: msg.recipient })
                        : t("common.chat.privateMessage")}
                    </div>
                  )}
                  <div style={{ wordBreak: "break-word" }}>{msg.text}</div>
                  <div
                    style={{
                      position: "absolute",
                      right: "8px",
                      bottom: "4px",
                      fontSize: "var(--text-2xs)",
                      fontWeight: "800",
                      color: isMe ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)",
                      fontFamily: "var(--font-mono)",
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    {msg.tc}
                    {isMe && (
                      <i className="bx bx-check-double" style={{ fontSize: "var(--text-sm)" }}></i>
                    )}
                  </div>
                </div>

                {/* Reply Button - Hidden by default, shown on hover */}
                <button
                  data-testid={`chat-btn-reply-${idx}`}
                  className="reply-btn"
                  onClick={() => {
                    setReplyingTo(msg);
                    setTarget(isGlobal ? "Global" : isMe ? msg.recipient || "Global" : msg.author);
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    [isMe ? "left" : "right"]: "-35px",
                    transform: "translateY(-50%)",
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    color: "white",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    opacity: 0,
                    transition: "all 0.2s",
                    zIndex: 3,
                  }}
                  title="Reply"
                >
                  <i className="bx bx-reply" style={{ fontSize: "1rem" }}></i>
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Input & Footer Controls */}
      <div
        style={{
          padding: "16px",
          borderTop: "1px solid var(--color-panel-border)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          background: "var(--color-bg-base)",
          position: "relative",
        }}
      >
        {/* Controls: Target & Copy */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "space-between",
            position: "relative",
            zIndex: 20,
          }}
        >
          {/* Custom Dropdown */}
          <div style={{ position: "relative", flex: 1 }}>
            {showTargetSelector && (
              <div
                style={{ position: "fixed", inset: 0, zIndex: 10 }}
                onClick={() => setShowTargetSelector(false)}
              />
            )}
            <button
              data-testid="chat-target-selector"
              type="button"
              onClick={() => setShowTargetSelector(!showTargetSelector)}
              style={{
                width: "100%",
                background: "var(--color-panel-bg)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "4px",
                padding: "6px 12px",
                color: "white",
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "var(--text-sm)",
                zIndex: 11,
                position: "relative",
              }}
            >
              <span style={{ fontWeight: "600" }}>
                {target === "Global"
                  ? t("common.chat.global", { count: activeUsers.length })
                  : target}
              </span>
              <i className={`bx bx-chevron-${showTargetSelector ? "up" : "down"}`}></i>
            </button>

            {showTargetSelector && (
              <div
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  width: "100%",
                  background: "var(--color-bg-base)",
                  border: "1px solid var(--color-panel-border)",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 20,
                  boxShadow: "0 -4px 20px rgba(0,0,0,0.5)",
                  marginBottom: "4px",
                }}
                className="custom-scrollbar"
              >
                <div
                  onClick={() => {
                    setTarget("Global");
                    setShowTargetSelector(false);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "var(--text-sm)",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                  className="hover-item"
                >
                  {t("common.chat.global", { count: activeUsers.length })}
                </div>
                {((activeUsers as any[]) || [])
                  .map((u) => (typeof u === "string" ? u : u.name))
                  .filter((u: string) => u && u !== user?.name)
                  .map((u: string) => (
                    <div
                      key={u}
                      onClick={() => {
                        setTarget(u);
                        setShowTargetSelector(false);
                      }}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: "var(--text-sm)" }}
                      className="hover-item"
                    >
                      {u}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <button
            data-testid="chat-btn-copy"
            onClick={handleCopyResponse}
            style={{
              padding: "4px 12px",
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "4px",
              color: "white",
              fontSize: "var(--text-sm)",
              fontWeight: "700",
              letterSpacing: "1px",
              cursor: "pointer",
            }}
            className="hover-btn"
            title={t("common.chat.sendCopy")}
          >
            {t("common.chat.copy")}
          </button>

          <button
            data-testid="chat-btn-emoji"
            onClick={() => setShowEmoji(!showEmoji)}
            style={{
              background: "none",
              border: "none",
              color: showEmoji ? "var(--color-accent-danger)" : "rgba(255,255,255,0.5)",
              cursor: "pointer",
              fontSize: "1.2rem",
              padding: "0 4px",
            }}
          >
            <i className="bx bx-smile"></i>
          </button>
        </div>

        {/* Emoji Picker Popover */}
        {showEmoji && (
          <div
            ref={emojiRef}
            style={{ position: "absolute", bottom: "110px", right: "10px", zIndex: 10 }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme={Theme.DARK}
              width={300}
              height={350}
            />
          </div>
        )}

        {/* Replying To Bar */}
        {replyingTo && (
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              borderLeft: "3px solid var(--color-accent-danger)",
              padding: "8px 12px",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "4px",
              animation: "fadeIn 0.2s ease",
            }}
          >
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontSize: "var(--text-2xs)",
                  fontWeight: "800",
                  color: "var(--color-accent-danger)",
                  letterSpacing: "0.5px",
                }}
              >
                {t("common.chat.replyingTo", { author: replyingTo.author.toUpperCase() })}
              </div>
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  opacity: 0.6,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {replyingTo.text}
              </div>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.5)",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <i className="bx bx-x" style={{ fontSize: "var(--text-lg)" }}></i>
            </button>
          </div>
        )}

        <form
          onSubmit={handleSend}
          style={{
            display: "flex",
            gap: "10px",
          }}
        >
          <input
            data-testid="chat-input"
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={t("common.chat.messagePlaceholder", {
              target: target === "Global" ? "Global" : target,
            })}
            autoFocus
            style={{
              flex: 1,
              background: "var(--color-input-bg)",
              border: "1px solid var(--color-panel-border)",
              borderColor:
                target !== "Global" ? "var(--color-accent-warning)" : "var(--color-panel-border)",
              borderRadius: "6px",
              padding: "12px",
              color: "white",
              fontSize: "var(--text-base)",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) =>
              (e.target.style.borderColor =
                target !== "Global" ? "var(--color-accent-warning)" : "var(--color-accent-danger)")
            }
            onBlur={(e) =>
              (e.target.style.borderColor =
                target !== "Global" ? "var(--color-accent-warning)" : "var(--color-panel-border)")
            }
          />
          <button
            data-testid="chat-btn-send"
            type="submit"
            disabled={!inputText.trim()}
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              backgroundColor: inputText.trim()
                ? "var(--color-accent-danger)"
                : "rgba(255, 255, 255, 0.03)",
              color: inputText.trim() ? "#fff" : "var(--color-accent-danger)",
              border: `1px solid ${inputText.trim() ? "transparent" : "rgba(247, 45, 96, 0.3)"}`,
              cursor: inputText.trim() ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              boxShadow: inputText.trim() ? "0 8px 16px rgba(247, 45, 96, 0.4)" : "none",
            }}
            onMouseEnter={(e) => {
              if (inputText.trim()) {
                e.currentTarget.style.transform = "scale(1.1) translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 24px rgba(247, 45, 96, 0.5)";
              }
            }}
            onMouseLeave={(e) => {
              if (inputText.trim()) {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(247, 45, 96, 0.4)";
              }
            }}
          >
            <i
              className="bx bxs-paper-plane"
              style={{
                fontSize: "1.4rem",
                transform: inputText.trim() ? "rotate(15deg) scale(1.1)" : "rotate(0deg)",
                transition: "all 0.4s ease",
                opacity: inputText.trim() ? 1 : 0.6,
              }}
            ></i>
          </button>
        </form>
      </div>

      <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .hover-item:hover {
                    background-color: rgba(255,255,255,0.1);
                }
                .hover-btn:hover {
                    background-color: rgba(255,255,255,0.2) !important;
                    color: white !important;
                }
                .hover-btn-close:hover {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                    color: white !important;
                    border-color: rgba(255, 255, 255, 0.3) !important;
                }
            `}</style>
    </div>
  );
};
